import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

/* Self-contained hero video player with custom controls:
   play/pause, seek progress bar, volume and fullscreen. Rounded + shadowed. */
export const HeroVideoPlayer = ({ src, poster }) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => { setProgress(v.currentTime); setDuration(v.duration || 0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [src]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.volume === 0) { v.volume = 1; setVolume(1); }
  };

  const onVolume = (e) => {
    const val = Number(e.target.value);
    const v = videoRef.current;
    if (v) { v.volume = val; v.muted = val === 0; }
    setVolume(val);
    setMuted(val === 0);
  };

  const onSeek = (e) => {
    const val = Number(e.target.value);
    const v = videoRef.current;
    if (v) v.currentTime = val;
    setProgress(val);
  };

  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const fmt = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div ref={wrapRef} data-testid="hero-video-player"
      className="group relative w-full overflow-hidden rounded-3xl border border-white/60 shadow-2xl shadow-[#0b132b]/25 bg-black aspect-video">
      <video ref={videoRef} key={src} src={src} poster={poster} muted={muted} loop playsInline autoPlay
        onClick={togglePlay} className="w-full h-full object-cover cursor-pointer" data-testid="hero-player-video" />

      {/* Center play/pause overlay */}
      <button type="button" onClick={togglePlay} data-testid="hero-player-toggle" aria-label={playing ? "Pause" : "Play"}
        className={`absolute inset-0 m-auto w-16 h-16 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-[#0b132b] shadow-xl transition-all duration-300 ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
        {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 translate-x-0.5" fill="currentColor" />}
      </button>

      {/* Bottom control bar */}
      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 bg-gradient-to-t from-black/70 via-black/25 to-transparent">
        <input type="range" min={0} max={duration || 0} step={0.1} value={progress} onChange={onSeek}
          data-testid="hero-player-seek" aria-label="Seek"
          className="w-full h-1.5 accent-[var(--brand)] cursor-pointer" />
        <div className="mt-2 flex items-center gap-3 text-white">
          <button type="button" onClick={togglePlay} className="shrink-0 hover:text-[var(--brand)] transition-colors" aria-label={playing ? "Pause" : "Play"}>
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" fill="currentColor" />}
          </button>
          <span className="text-[11px] font-semibold tabular-nums" data-testid="hero-player-time">{fmt(progress)} / {fmt(duration)}</span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={toggleMute} data-testid="hero-player-mute" className="hover:text-[var(--brand)] transition-colors" aria-label={muted ? "Unmute" : "Mute"}>
              {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={onVolume}
              data-testid="hero-player-volume" aria-label="Volume" className="w-14 h-1.5 accent-[var(--brand)] cursor-pointer hidden sm:block" />
            <button type="button" onClick={toggleFullscreen} data-testid="hero-player-fullscreen" className="hover:text-[var(--brand)] transition-colors" aria-label="Fullscreen">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
